import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getRestaurantClients } from "../actions/userActions";
import ShowCustomerInfo from "../components/ShowCustomerInfo";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, Row, Col, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPhone, faCopy, faEye, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { Container, CardHeader, CardHeaderTitle } from "../components/DefaultImports";
const BOTTOM_PADDING = 40;
function RestaurantClients() {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const [restaurant_id, setRestaurant_id] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isShowCustomerInfoModalActive, setIsShowCustomerInfoModalActive] = useState(false);
  const [completePhone, setCompletePhone] = useState("");
  const clients = useSelector(state => state.restaurant_clients);
  const {
    restaurantClients,
    isLoadingGetRestaurantClients
  } = clients;
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurantDetails;
  const totalPages = restaurantClients ? Math.ceil(restaurantClients.total_results / 10) : 0;
  const clientListRef = useRef(null);
  const [listHeight, setListHeight] = useState(0);
  useEffect(() => {
    if (restaurants) {
      setRestaurant_id(restaurants.id);
    }
  }, [restaurants]);
  useEffect(() => {
    if (restaurant_id) {
      dispatch(getRestaurantClients(restaurant_id, currentPage, debouncedSearchTerm));
    }
  }, [dispatch, restaurant_id, currentPage, debouncedSearchTerm]);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchTerm]);
  useLayoutEffect(() => {
    if (!clientListRef.current) return;
    const updateHeight = () => {
      if (!clientListRef.current) return;
      const top = clientListRef.current.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - top - BOTTOM_PADDING;
      setListHeight(Math.max(0, availableHeight));
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);
    const ro = new ResizeObserver(() => updateHeight());
    ro.observe(document.body);
    const raf = requestAnimationFrame(updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [restaurantClients, isLoadingGetRestaurantClients]);
  const handleSearchChange = e => setSearchTerm(e.target.value);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(p => p - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(p => p + 1);
  const copyToClipboard = text => {
    navigator.clipboard.writeText(text);
    toast.success("Número copiado!", {
      autoClose: 1000,
      icon: <FontAwesomeIcon icon={faCheckCircle} style={{
        color: "#EE754C"
      }} />,
      style: {
        color: "#EE754C"
      },
      progressStyle: {
        backgroundColor: "#EE754C"
      }
    });
  };
  const showCustomerInfo = phone_number => {
    setCompletePhone(phone_number);
    setIsShowCustomerInfoModalActive(true);
  };
  const paginatedResults = restaurantClients?.results || [];
  return <Container>
      <ToastContainer />
      <CardHeader>
        <CardHeaderTitle>{t("ui.screens.restaurantClients.clientes")}</CardHeaderTitle>
      </CardHeader>

      <SearchContainer>
        <SearchInput type="text" placeholder={t("ui.screens.restaurantClients.procurarCliente")} value={searchTerm} onChange={handleSearchChange} />
        <SearchIcon icon={faSearch} />
      </SearchContainer>

      {restaurantClients?.total_results > 0 && <div style={{
      display: "flex",
      justifyContent: "center",
      marginBottom: "10px"
    }}>
          <FixedPagination>
            <PageSide>
              {currentPage > 1 ? <PageButton onClick={handlePrevPage}>{t("ui.screens.restaurantClients.anterior")}</PageButton> : <PagePlaceholder />}
            </PageSide>

            <PageCenter>
              <PageNumber>{currentPage}</PageNumber>
            </PageCenter>

            <PageSide>
              {currentPage < totalPages ? <PageButton onClick={handleNextPage}>{t("ui.screens.restaurantClients.proxima")}</PageButton> : <PagePlaceholder />}
            </PageSide>
          </FixedPagination>
        </div>}

      {isLoadingGetRestaurantClients ? <div className="text-center my-5">
          <Spinner animation="border" />
          <p>{t("ui.screens.restaurantClients.carregandoClientes")}</p>
        </div> : <ClientListWrapper ref={clientListRef} height={listHeight}>
          {paginatedResults.length > 0 ? paginatedResults.map(client => <ClientCard key={client.id}>
                <Row>
                  <Col md={5} xs={12} className="d-flex align-items-center gap-2 mb-2 mb-md-0">
                    <IconAction icon={faEye} onClick={() => showCustomerInfo(client.client.phone_number)} />
                    <strong>{client.client.name}</strong>
                  </Col>
                  <Col md={4} xs={12} className="d-flex align-items-center gap-2 text-muted mb-2 mb-md-0">
                    <IconAction icon={faPhone} />+
                    {client.client.phone_number.slice(0, 3)}{" "}
                    {client.client.phone_number.slice(3)}
                    <IconAction icon={faCopy} onClick={() => copyToClipboard(client.client.phone_number)} />
                  </Col>
                  <Col md={3} xs={12} className="d-flex justify-content-md-end gap-2">
                    <CustomBadge bg="#EE754C">{t("ui.screens.restaurantClients.reservas")}{client.number_of_reservations}
                    </CustomBadge>
                    <CustomBadge bg="#99b0ad">{t("ui.screens.restaurantClients.naoCompareceu")}{client.did_not_show_up_times}
                    </CustomBadge>
                  </Col>
                </Row>
              </ClientCard>) : <p className="text-center mt-4">{t("ui.screens.restaurantClients.nenhumClienteEncontrado")}</p>}
        </ClientListWrapper>}

      <ShowCustomerInfo phone_number={completePhone} restaurant_id={restaurant_id} isModalActive={isShowCustomerInfoModalActive} setIsModalActive={setIsShowCustomerInfoModalActive} />
    </Container>;
}
export default RestaurantClients;
const SearchContainer = styled.div`
  max-width: 500px;
  margin: 20px auto 30px;
  position: relative;
`;
const SearchInput = styled.input`
  width: 100%;
  padding: 12px 44px 12px 16px;
  border-radius: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  font-size: 15px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #ee754c;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.15);
  }
`;
const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
`;
const ClientCard = styled(Card)`
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: 0.2s;

  &:hover {
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.07);
  }
`;
const IconAction = styled(FontAwesomeIcon)`
  color: #ee754c;
  cursor: pointer;
`;
const ClientListWrapper = styled.div`
  height: ${props => props.height}px; /* já vem com espaço do BOTTOM_PADDING abatido */
  overflow-y: auto;
  padding: 10px;
`;
const CustomBadge = styled.span`
  background-color: ${props => props.bg || "#ccc"};
  color: white;
  padding: 4px 10px;
  font-size: 14px;
  border-radius: 12px;
  font-weight: 500;
  display: inline-block;
  white-space: nowrap;
`;
const FixedPagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
`;
const PageSide = styled.div`
  display: flex;
  justify-content: center;
  min-width: 120px;
`;
const PageCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const PageButton = styled.div`
  background-color: #f2f2f2;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;

  &:hover {
    background-color: #e0e0e0;
  }
`;
const PageNumber = styled.div`
  background-color: #ee754c;
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
  text-align: center;
  min-width: 40px;
`;
const PagePlaceholder = styled.div`
  visibility: hidden;
  min-width: 90px;
`;
